import { FC, useMemo } from "react";
import { AssortmentTaskType, TaskAssortmentMatrix } from "tm-shared/types";

import { StepActionButton } from "@/domains/tasks";
import { useCompleteTask, useTaskConfigSelector } from "@/domains/tasks/hooks";
import { makeCommand, useAsyncAction, useMakeCommand } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { Loading } from "@/shared/feedback/Loading";
import { setMdTasksConfig } from "@/store/slices/ui";

import { getNextSubtaskId } from "../AssortmentMatrix.utils";

interface StepButtonProps {
    task: TaskAssortmentMatrix;
    pointId: string;
    currentCompleted?: boolean;
}

export const StepButton: FC<StepButtonProps> = ({
    task,
    pointId,
    currentCompleted = false,
}) => {
    const dispatch = useAppDispatch();

    const taskId = task.id;
    const subtaskIds = task.context.subtasks.map((t) => t.subtaskId);

    const { activeItem: activeStepKey } = useTaskConfigSelector({
        pointId,
        taskId,
    });

    const taskPayload = useAppSelector(
        (s) => s.progress.assortmentMatrix[taskId],
    );

    const allCompleted = useMemo(() => {
        if (!taskPayload) return false;

        const subtaskPayloads = Object.values(taskPayload);

        if (subtaskPayloads.length !== subtaskIds.length) return false;

        for (const p of subtaskPayloads) {
            if (!p.completed) return false;
        }

        return true;
    }, [taskPayload, subtaskIds]);

    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();

    const completeTask = useCompleteTask({ targetId: pointId, taskId });

    const onNext = () => {
        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                field: "activeItem",
                value: nextSubtaskId,
            }),
        );
    };

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        if (!taskPayload) return;

        const photoIds = Object.values(taskPayload).reduce<string[]>(
            (result, item) => {
                if (item.type === AssortmentTaskType.FinGroupPhotos) {
                    const currentIds = Object.values(item.tasks).flatMap(
                        (task) => task?.photos ?? [],
                    );

                    return [...result, ...currentIds];
                }

                return result;
            },
            [],
        );

        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto, payload: taskPayload },
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),

            ...taskCompleteCommands,

            makeCommand({
                type: "updatePhotoStorage",
                title: "Синхронізація сховища",
                params: photoIds,
                options: {
                    stopOnError: false,
                },
            }),
        ];

        completeTask({ commands });
    });

    if (!activeStepKey) return null;

    const { nextSubtaskId, isLast } = getNextSubtaskId({
        subtasks: task.context.subtasks,
        currentSubtaskId: activeStepKey,
    });

    const buttonDisabled = isLast && !allCompleted && currentCompleted;

    return (
        <>
            {currentCompleted && (
                <StepActionButton
                    onNext={onNext}
                    onFinish={handleComplete}
                    isLast={isLast}
                    disabled={buttonDisabled}
                    loading={completeLoading}
                />
            )}

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
